import {
  type Message,
  MethodKind,
  proto3,
  Timestamp,
} from "@bufbuild/protobuf";
import { PageParams } from "@buf/brevdev_devplane.bufbuild_es/devplaneapi/v1/common_pb";

/**
 * Local descriptors for ManagedSecretService.
 *
 * The backend contract is newer than the currently published Buf npm package.
 * Remove this file and use the generated managed_secret modules once that
 * package is available to the console.
 */
export const ManagedSecretAccessLevel = proto3.makeEnum(
  "devplaneapi.v1.ManagedSecretAccessLevel",
  [
    {
      no: 0,
      name: "MANAGED_SECRET_ACCESS_LEVEL_UNSPECIFIED",
      localName: "UNSPECIFIED",
    },
    {
      no: 1,
      name: "MANAGED_SECRET_ACCESS_LEVEL_MAINTAINER",
      localName: "MAINTAINER",
    },
    {
      no: 2,
      name: "MANAGED_SECRET_ACCESS_LEVEL_READER",
      localName: "READER",
    },
  ],
);

export interface ManagedSecretUserAccess extends Message<ManagedSecretUserAccess> {
  userId: string;
  accessLevel: number;
}

export const ManagedSecretUserAccess =
  proto3.makeMessageType<ManagedSecretUserAccess>(
    "devplaneapi.v1.ManagedSecretUserAccess",
    () => [
      { no: 1, name: "user_id", kind: "scalar", T: 9 },
      {
        no: 2,
        name: "access_level",
        kind: "enum",
        T: proto3.getEnumType(ManagedSecretAccessLevel),
      },
    ],
  );

export interface ManagedSecret extends Message<ManagedSecret> {
  secretId: string;
  name: string;
  createTime?: Timestamp;
  updateTime?: Timestamp;
  organizationId: string;
  latestVersionId: string;
  latestVersionNumber: bigint;
  userAccesses: ManagedSecretUserAccess[];
}

export const ManagedSecret = proto3.makeMessageType<ManagedSecret>(
  "devplaneapi.v1.ManagedSecret",
  () => [
    { no: 1, name: "secret_id", kind: "scalar", T: 9 },
    { no: 2, name: "name", kind: "scalar", T: 9 },
    { no: 3, name: "create_time", kind: "message", T: Timestamp },
    { no: 4, name: "update_time", kind: "message", T: Timestamp },
    { no: 6, name: "organization_id", kind: "scalar", T: 9 },
    { no: 7, name: "latest_version_id", kind: "scalar", T: 9 },
    { no: 8, name: "latest_version_number", kind: "scalar", T: 3 },
    {
      no: 9,
      name: "user_accesses",
      kind: "message",
      T: ManagedSecretUserAccess,
      repeated: true,
    },
  ],
);

export interface ManagedSecretVersion extends Message<ManagedSecretVersion> {
  versionId: string;
  managedSecretId: string;
  versionNumber: bigint;
  createTime?: Timestamp;
}

export const ManagedSecretVersion =
  proto3.makeMessageType<ManagedSecretVersion>(
    "devplaneapi.v1.ManagedSecretVersion",
    () => [
      { no: 1, name: "version_id", kind: "scalar", T: 9 },
      { no: 2, name: "managed_secret_id", kind: "scalar", T: 9 },
      { no: 3, name: "version_number", kind: "scalar", T: 3 },
      { no: 4, name: "create_time", kind: "message", T: Timestamp },
    ],
  );

export interface CreateSecretRequest extends Message<CreateSecretRequest> {
  name: string;
  value: string;
  organizationId: string;
}

export const CreateSecretRequest = proto3.makeMessageType<CreateSecretRequest>(
  "devplaneapi.v1.ManagedSecretServiceCreateSecretRequest",
  () => [
    { no: 1, name: "name", kind: "scalar", T: 9 },
    { no: 2, name: "value", kind: "scalar", T: 9 },
    { no: 3, name: "organization_id", kind: "scalar", T: 9 },
  ],
);

export interface CreateSecretResponse extends Message<CreateSecretResponse> {
  secret?: ManagedSecret;
}

export const CreateSecretResponse =
  proto3.makeMessageType<CreateSecretResponse>(
    "devplaneapi.v1.ManagedSecretServiceCreateSecretResponse",
    () => [{ no: 1, name: "secret", kind: "message", T: ManagedSecret }],
  );

export interface ListSecretsRequest extends Message<ListSecretsRequest> {
  organizationId: string;
  pageParams?: PageParams;
}

export const ListSecretsRequest = proto3.makeMessageType<ListSecretsRequest>(
  "devplaneapi.v1.ManagedSecretServiceListSecretsRequest",
  () => [
    { no: 1, name: "organization_id", kind: "scalar", T: 9 },
    { no: 2, name: "page_params", kind: "message", T: PageParams },
  ],
);

export interface ListSecretsResponse extends Message<ListSecretsResponse> {
  items: ManagedSecret[];
  nextPageToken: string;
}

export const ListSecretsResponse = proto3.makeMessageType<ListSecretsResponse>(
  "devplaneapi.v1.ManagedSecretServiceListSecretsResponse",
  () => [
    {
      no: 1,
      name: "items",
      kind: "message",
      T: ManagedSecret,
      repeated: true,
    },
    { no: 2, name: "next_page_token", kind: "scalar", T: 9 },
  ],
);

export interface GetSecretRequest extends Message<GetSecretRequest> {
  secretId: string;
}

export const GetSecretRequest = proto3.makeMessageType<GetSecretRequest>(
  "devplaneapi.v1.ManagedSecretServiceGetSecretRequest",
  () => [{ no: 1, name: "secret_id", kind: "scalar", T: 9 }],
);

export interface GetSecretResponse extends Message<GetSecretResponse> {
  secret?: ManagedSecret;
}

export const GetSecretResponse = proto3.makeMessageType<GetSecretResponse>(
  "devplaneapi.v1.ManagedSecretServiceGetSecretResponse",
  () => [{ no: 1, name: "secret", kind: "message", T: ManagedSecret }],
);

export interface ListSecretVersionsRequest extends Message<ListSecretVersionsRequest> {
  secretId: string;
  pageParams?: PageParams;
}

export const ListSecretVersionsRequest =
  proto3.makeMessageType<ListSecretVersionsRequest>(
    "devplaneapi.v1.ManagedSecretServiceListSecretVersionsRequest",
    () => [
      { no: 1, name: "secret_id", kind: "scalar", T: 9 },
      { no: 2, name: "page_params", kind: "message", T: PageParams },
    ],
  );

export interface ListSecretVersionsResponse extends Message<ListSecretVersionsResponse> {
  items: ManagedSecretVersion[];
  nextPageToken: string;
}

export const ListSecretVersionsResponse =
  proto3.makeMessageType<ListSecretVersionsResponse>(
    "devplaneapi.v1.ManagedSecretServiceListSecretVersionsResponse",
    () => [
      {
        no: 1,
        name: "items",
        kind: "message",
        T: ManagedSecretVersion,
        repeated: true,
      },
      { no: 2, name: "next_page_token", kind: "scalar", T: 9 },
    ],
  );

export interface GetSecretValueRequest extends Message<GetSecretValueRequest> {
  secretId: string;
  versionId: string;
}

export const GetSecretValueRequest =
  proto3.makeMessageType<GetSecretValueRequest>(
    "devplaneapi.v1.ManagedSecretServiceGetSecretValueRequest",
    () => [
      { no: 1, name: "secret_id", kind: "scalar", T: 9 },
      { no: 2, name: "version_id", kind: "scalar", T: 9 },
    ],
  );

export interface GetSecretValueResponse extends Message<GetSecretValueResponse> {
  value: string;
}

export const GetSecretValueResponse =
  proto3.makeMessageType<GetSecretValueResponse>(
    "devplaneapi.v1.ManagedSecretServiceGetSecretValueResponse",
    () => [{ no: 1, name: "value", kind: "scalar", T: 9 }],
  );

export interface SetSecretValueRequest extends Message<SetSecretValueRequest> {
  secretId: string;
  value: string;
}

export const SetSecretValueRequest =
  proto3.makeMessageType<SetSecretValueRequest>(
    "devplaneapi.v1.ManagedSecretServiceSetSecretValueRequest",
    () => [
      { no: 1, name: "secret_id", kind: "scalar", T: 9 },
      { no: 2, name: "value", kind: "scalar", T: 9 },
    ],
  );

export interface SetSecretValueResponse extends Message<SetSecretValueResponse> {
  secret?: ManagedSecret;
  version?: ManagedSecretVersion;
}

export const SetSecretValueResponse =
  proto3.makeMessageType<SetSecretValueResponse>(
    "devplaneapi.v1.ManagedSecretServiceSetSecretValueResponse",
    () => [
      { no: 1, name: "secret", kind: "message", T: ManagedSecret },
      { no: 2, name: "version", kind: "message", T: ManagedSecretVersion },
    ],
  );

export interface SetSecretAccessRequest extends Message<SetSecretAccessRequest> {
  secretId: string;
  userId: string;
  accessLevel: number;
}

export const SetSecretAccessRequest =
  proto3.makeMessageType<SetSecretAccessRequest>(
    "devplaneapi.v1.ManagedSecretServiceSetSecretAccessRequest",
    () => [
      { no: 1, name: "secret_id", kind: "scalar", T: 9 },
      { no: 2, name: "user_id", kind: "scalar", T: 9 },
      {
        no: 3,
        name: "access_level",
        kind: "enum",
        T: proto3.getEnumType(ManagedSecretAccessLevel),
      },
    ],
  );

export interface SetSecretAccessResponse extends Message<SetSecretAccessResponse> {
  secret?: ManagedSecret;
}

export const SetSecretAccessResponse =
  proto3.makeMessageType<SetSecretAccessResponse>(
    "devplaneapi.v1.ManagedSecretServiceSetSecretAccessResponse",
    () => [{ no: 1, name: "secret", kind: "message", T: ManagedSecret }],
  );

export interface RemoveSecretAccessRequest extends Message<RemoveSecretAccessRequest> {
  secretId: string;
  userId: string;
}

export const RemoveSecretAccessRequest =
  proto3.makeMessageType<RemoveSecretAccessRequest>(
    "devplaneapi.v1.ManagedSecretServiceRemoveSecretAccessRequest",
    () => [
      { no: 1, name: "secret_id", kind: "scalar", T: 9 },
      { no: 2, name: "user_id", kind: "scalar", T: 9 },
    ],
  );

export interface RemoveSecretAccessResponse extends Message<RemoveSecretAccessResponse> {
  secret?: ManagedSecret;
}

export const RemoveSecretAccessResponse =
  proto3.makeMessageType<RemoveSecretAccessResponse>(
    "devplaneapi.v1.ManagedSecretServiceRemoveSecretAccessResponse",
    () => [{ no: 1, name: "secret", kind: "message", T: ManagedSecret }],
  );

export interface DeleteSecretRequest extends Message<DeleteSecretRequest> {
  secretId: string;
}

export const DeleteSecretRequest = proto3.makeMessageType<DeleteSecretRequest>(
  "devplaneapi.v1.ManagedSecretServiceDeleteSecretRequest",
  () => [{ no: 1, name: "secret_id", kind: "scalar", T: 9 }],
);

export interface DeleteSecretResponse extends Message<DeleteSecretResponse> {}

export const DeleteSecretResponse =
  proto3.makeMessageType<DeleteSecretResponse>(
    "devplaneapi.v1.ManagedSecretServiceDeleteSecretResponse",
    [],
  );

export const ManagedSecretService = {
  typeName: "devplaneapi.v1.ManagedSecretService",
  methods: {
    createSecret: {
      name: "CreateSecret",
      I: CreateSecretRequest,
      O: CreateSecretResponse,
      kind: MethodKind.Unary,
    },
    listSecrets: {
      name: "ListSecrets",
      I: ListSecretsRequest,
      O: ListSecretsResponse,
      kind: MethodKind.Unary,
    },
    getSecret: {
      name: "GetSecret",
      I: GetSecretRequest,
      O: GetSecretResponse,
      kind: MethodKind.Unary,
    },
    listSecretVersions: {
      name: "ListSecretVersions",
      I: ListSecretVersionsRequest,
      O: ListSecretVersionsResponse,
      kind: MethodKind.Unary,
    },
    getSecretValue: {
      name: "GetSecretValue",
      I: GetSecretValueRequest,
      O: GetSecretValueResponse,
      kind: MethodKind.Unary,
    },
    setSecretValue: {
      name: "SetSecretValue",
      I: SetSecretValueRequest,
      O: SetSecretValueResponse,
      kind: MethodKind.Unary,
    },
    setSecretAccess: {
      name: "SetSecretAccess",
      I: SetSecretAccessRequest,
      O: SetSecretAccessResponse,
      kind: MethodKind.Unary,
    },
    removeSecretAccess: {
      name: "RemoveSecretAccess",
      I: RemoveSecretAccessRequest,
      O: RemoveSecretAccessResponse,
      kind: MethodKind.Unary,
    },
    deleteSecret: {
      name: "DeleteSecret",
      I: DeleteSecretRequest,
      O: DeleteSecretResponse,
      kind: MethodKind.Unary,
    },
  },
} as const;
